import React from "react";
import { CatalogModal, CatalogItem } from "../components/AssetModal/AssetModal";
import { PageHeader } from "../components/PageHeader/PageHeader";
import { FormField, SelectField, TextareaField, DateField } from "@makula/ui";
import { ContractCard, ContractDetailPanel } from "../components/ContractDetailPanel/ContractDetailPanel";
import { SendEmailModal, MOCK_SENDER_ACCOUNTS } from "../components/SendEmailModal/SendEmailModal";
import { useFinanceConfig } from "../context/FinanceConfigContext";
import type { PriceBook } from "../context/FinanceConfigContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LineItem {
  id: string;
  catalogId?: string;
  name: string;
  qty: number;
  unit: string;
  price: number;
  discount: number;
  margin: number;
}

interface InvoiceBuilderPageProps {
  invoiceNumber?: string;
  onBack?: () => void;
  poNumber?: string;
  poDocument?: File;
  initialStatus?: InvoiceStatus;
  linkedWo?: { number: string; title: string };
  onNavigateToWorkOrder?: (woNumber: string) => void;
  initialLineItems?: LineItem[];
  onEmailSent?: (data: { docType: "Invoice"; docNumber: string; fromEmail: string; toEmail: string; toName: string; subject: string; body: string }) => void;
}

type InvoiceStatus = 'draft' | 'sent' | 'changes_requested' | 'approved' | 'paid' | 'overdue';

interface ChangeRequestInfo {
  message: string;
  customerName: string;
  timestamp: string;
}

interface ApprovalInfo {
  approvedAt: string;
  approvedBy: string;
}

interface PaymentRecord {
  id: string;
  amount: number;
  dateReceived: string;
  method: string;
  reference: string;
  note: string;
}

type ContractType = "company" | "asset";
type ContractStatus = "active" | "expiring_soon" | "expired" | "draft";
type SLAPriority = "Critical" | "High" | "Medium" | "Low";
type SLATimeUnit = "hours" | "days";

interface SLALevel {
  id: string;
  priority: SLAPriority;
  responseTime: number;
  responseUnit: SLATimeUnit;
  resolutionTime: number;
  resolutionUnit: SLATimeUnit;
}

interface Contract {
  id: string;
  contractNumber: string;
  type: ContractType;
  status: ContractStatus;
  assetId?: string;
  assetName?: string;
  effectiveDate: string;
  expiryDate: string;
  laborRate: number;
  partsDiscountRate: number;
  travelRate: number;
  paymentTerms: string;
  slaLevels: SLALevel[];
}

// ─── Date Helpers ─────────────────────────────────────────────────────────────

function todayFormatted(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function addDaysToFormatted(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function parsePaymentDays(paymentTerms: string): number {
  const match = paymentTerms.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const PdfIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
    <path d="M4 5h6M4 7.5h4M4 10h3" />
  </svg>
);

const DuplicateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="8" height="8" rx="1.5" />
    <path d="M2 10V3a1 1 0 011-1h7" />
  </svg>
);

const MoreIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
    <circle cx="3" cy="7" r="1.2" />
    <circle cx="7" cy="7" r="1.2" />
    <circle cx="11" cy="7" r="1.2" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M6.5 1.5v10M1.5 6.5h10" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3.5h10M5.5 3.5V2.5a.5.5 0 01.5-.5h2a.5.5 0 01.5.5v1M3 3.5l.5 8a.5.5 0 00.5.5h6a.5.5 0 00.5-.5l.5-8" />
    <path d="M6 6v4M8 6v4" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round">
    <path d="M3.5 5.5l3.5 3.5 3.5-3.5" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#6B7280" strokeWidth="1.3" strokeLinecap="round">
    <path d="M3.5 8.5l3.5-3.5 3.5 3.5" />
  </svg>
);

const CalendarDetailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" />
    <path d="M1.5 5.5h11M4.5 1.5v2M9.5 1.5v2" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="5" r="2.5" />
    <path d="M2 12c0-2.761 2.239-5 5-5s5 2.239 5 5" />
  </svg>
);

const StatusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="7" r="5" />
    <path d="M7 4.5V7l2 1.5" />
  </svg>
);

const CompanyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="10" height="8" rx="1" />
    <path d="M5 4V3a2 2 0 014 0v1" />
  </svg>
);

const ContactIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="4.5" r="2" />
    <path d="M2.5 12c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
  </svg>
);

const WorkOrderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
    <path d="M4.5 5h5M4.5 7.5h5M4.5 10h3" />
  </svg>
);

const PaymentIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="3.5" width="11" height="7" rx="1.5" />
    <path d="M1.5 6.5h11" />
  </svg>
);

const ContractIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
    <path d="M4.5 4.5h5M4.5 7h5M4.5 9.5h2.5" />
    <circle cx="9.5" cy="9.5" r="1.5" fill="none" />
    <path d="M10.5 10.5l1 1" />
  </svg>
);

const HashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round">
    <path d="M2.5 5h9M2.5 9h9M5 2.5l-1 9M10 2.5l-1 9" />
  </svg>
);



const QuoteDocIcon = ({ color = "#6974fb" }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
    <path d="M4.5 5h5M4.5 7.5h5M4.5 10h3" />
  </svg>
);

const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 6.5l4 1.5 1.5 4L12 2z" />
    <path d="M6 8l2.5-2.5" />
  </svg>
);


// ─── Shared styles ────────────────────────────────────────────────────────────

const inputBase: React.CSSProperties = {
  width: "100%",
  padding: "var(--spacing-m)",
  border: "1px solid transparent",
  borderRadius: "var(--radius-s)",
  background: "var(--color-bg-accent)",
  fontFamily: "var(--font-family-base)",
  fontSize: "var(--font-size-label-s)",
  fontWeight: "var(--font-weight-medium)",
  lineHeight: "var(--line-height-label-s)",
  color: "var(--color-content-primary)",
  outline: "none",
  boxSizing: "border-box",
  appearance: "none" as const,
  transition: "border-color 120ms ease, background-color 120ms ease",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontFamily: "var(--font-family-base)",
  fontSize: "var(--font-size-label-s)",
  fontWeight: "var(--font-weight-medium)",
  lineHeight: "var(--line-height-label-s)",
  color: "var(--color-content-secondary)",
};

const fieldWrap: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-s)",
  width: "100%",
};

// ─── Sub-components ───────────────────────────────────────────────────────────


const UNIT_OPTIONS = [
  { value: "each",  label: "Each"  },
  { value: "hour",  label: "Hour"  },
  { value: "trip",  label: "Trip"  },
  { value: "day",   label: "Day"   },
  { value: "kg",    label: "KG"    },
  { value: "liter", label: "Liter" },
  { value: "meter", label: "Meter" },
];

function Avatar({ initials, color }: { initials: string; color: string }) {
  return (
    <div style={{
      width: 20, height: 20, borderRadius: "50%",
      background: color, color: "#fff",
      fontSize: 9, fontWeight: 700,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, fontFamily: "var(--font-family-base)",
    }}>
      {initials}
    </div>
  );
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", alignItems: "center", gap: 8, paddingBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {icon}
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>{label}</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: "#111827", fontFamily: "var(--font-family-base)" }}>{value}</div>
    </div>
  );
}

function TotalRow({ label, value, bold }: { label: string; value: string; bold: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", maxWidth: 320, alignSelf: "flex-end" }}>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 400, color: bold ? "#111827" : "#6B7280", fontFamily: "var(--font-family-base)" }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 400, color: bold ? "#111827" : "#374151", fontFamily: "var(--font-family-base)", minWidth: 80, textAlign: "right" }}>
        {value}
      </span>
    </div>
  );
}

function LineItemRow({ item, onUpdate, onDelete, rowTotal }: {
  item: LineItem;
  onUpdate: (id: string, field: keyof LineItem, value: string | number) => void;
  onDelete: (id: string) => void;
  rowTotal: number;
}) {
  const [hovering, setHovering] = React.useState(false);

  const cellInput: React.CSSProperties = {
    width: "100%", height: 30, padding: "0 8px",
    border: "1px solid #E5E7EB", borderRadius: 4,
    background: "#fff", fontFamily: "var(--font-family-base)",
    fontSize: 13, fontWeight: 400, color: "#111827",
    outline: "none", boxSizing: "border-box", appearance: "none" as const,
  };

  const fmtPrice = (n: number) =>
    `€ ${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 68px 108px 104px 88px 88px 88px 36px",
        borderBottom: "1px solid #F3F4F6",
        alignItems: "center",
        minHeight: 48,
        background: hovering ? "#F9FAFB" : "#fff",
        transition: "background 80ms",
      }}
    >
      <div style={{ padding: "8px 10px" }}>
        <input type="text" value={item.name} onChange={(e) => onUpdate(item.id, "name", e.target.value)} style={cellInput} />
      </div>
      <div style={{ padding: "8px 8px" }}>
        <input type="number" min={0} value={item.qty} onChange={(e) => onUpdate(item.id, "qty", Number(e.target.value))}
          style={{ ...cellInput, textAlign: "center", paddingLeft: 4, paddingRight: 4 }} />
      </div>
      <div style={{ padding: "8px 8px" }}>
        <SelectField
          value={item.unit}
          onChange={(v) => onUpdate(item.id, "unit", v)}
          options={UNIT_OPTIONS}
        />
      </div>
      <div style={{ padding: "8px 8px" }}>
        <div style={{ ...cellInput, display: "flex", alignItems: "center", gap: 4, padding: "0 8px", cursor: "text" }}
          onClick={(e) => (e.currentTarget.querySelector("input") as HTMLInputElement)?.focus()}>
          <span style={{ fontSize: 12, color: "#6B7280", flexShrink: 0 }}>€</span>
          <input type="number" min={0} step={0.01} value={item.price}
            onChange={(e) => onUpdate(item.id, "price", Number(e.target.value))}
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-family-base)", fontSize: 13, color: "#111827", width: "100%", textAlign: "right" }} />
        </div>
      </div>
      <div style={{ padding: "8px 8px" }}>
        <div style={{ ...cellInput, display: "flex", alignItems: "center", padding: "0 8px", cursor: "text" }}
          onClick={(e) => (e.currentTarget.querySelector("input") as HTMLInputElement)?.focus()}>
          <input type="number" min={0} max={100} value={item.discount}
            onChange={(e) => onUpdate(item.id, "discount", Number(e.target.value))}
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-family-base)", fontSize: 13, color: "#111827", width: "100%", textAlign: "right" }} />
          <span style={{ fontSize: 12, color: "#6B7280", flexShrink: 0 }}>%</span>
        </div>
      </div>
      {/* Margin */}
      <div style={{ padding: "8px 8px" }}>
        <div style={{ ...cellInput, display: "flex", alignItems: "center", padding: "0 8px", cursor: "text" }}
          onClick={(e) => (e.currentTarget.querySelector("input") as HTMLInputElement)?.focus()}>
          <input type="number" min={0} max={100} value={item.margin}
            onChange={(e) => onUpdate(item.id, "margin", Number(e.target.value))}
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-family-base)", fontSize: 13, color: "#111827", width: "100%", textAlign: "right" }} />
          <span style={{ fontSize: 12, color: "#6B7280", flexShrink: 0 }}>%</span>
        </div>
      </div>
      <div style={{ padding: "8px 10px", fontSize: 13, fontWeight: 400, color: "#111827", textAlign: "right", fontFamily: "var(--font-family-base)" }}>
        {fmtPrice(rowTotal)}
      </div>
      <div style={{ padding: "8px 6px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <button onClick={() => onDelete(item.id)}
          style={{ width: 24, height: 24, border: "none", borderRadius: 4, background: "transparent", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: hovering ? "#EF4444" : "transparent", transition: "color 120ms ease" }}>
          <TrashIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Status Badge Dropdown ────────────────────────────────────────────────────

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; bg: string; color: string }> = {
  draft:             { label: 'DRAFT',              bg: '#F3F4F6', color: '#6B7280' },
  sent:              { label: 'SENT',               bg: '#E6E8FE', color: '#0517F8' },
  changes_requested: { label: 'CHANGES REQUESTED',  bg: '#FFF7ED', color: '#C2410C' },
  approved:          { label: 'APPROVED',           bg: '#D1FAE5', color: '#059669' },
  paid:              { label: 'PAID',               bg: '#CCEFEB', color: '#0D9488' },
  overdue:           { label: 'OVERDUE',            bg: '#FEE2E2', color: '#DC2626' },
};

const DEFAULT_SLA_LEVELS_IB: SLALevel[] = [
  { id: "sla-1", priority: "Critical", responseTime: 4,  responseUnit: "hours", resolutionTime: 8,  resolutionUnit: "hours" },
  { id: "sla-2", priority: "High",     responseTime: 8,  responseUnit: "hours", resolutionTime: 24, resolutionUnit: "hours" },
  { id: "sla-3", priority: "Medium",   responseTime: 24, responseUnit: "hours", resolutionTime: 3,  resolutionUnit: "days"  },
  { id: "sla-4", priority: "Low",      responseTime: 48, responseUnit: "hours", resolutionTime: 5,  resolutionUnit: "days"  },
];

const COMPANY_CONTRACTS: Record<string, Contract[]> = {
  "acme-international": [
    {
      id: "c1", contractNumber: "CTR-2024-0041", type: "company", status: "active",
      effectiveDate: "2024-01-01", expiryDate: "2026-12-31", paymentTerms: "Net 30",
      laborRate: 85, partsDiscountRate: 15, travelRate: 1.2, slaLevels: DEFAULT_SLA_LEVELS_IB,
    },
    {
      id: "c2", contractNumber: "CTR-2025-0012", type: "asset", status: "expiring_soon",
      assetId: "M-2152", assetName: "CNC Machine A",
      effectiveDate: "2025-03-01", expiryDate: "2026-05-28", paymentTerms: "Net 60",
      laborRate: 95, partsDiscountRate: 20, travelRate: 1.5,
      slaLevels: [
        { id: "s1", priority: "Critical", responseTime: 2, responseUnit: "hours", resolutionTime: 4,  resolutionUnit: "hours" },
        { id: "s2", priority: "High",     responseTime: 4, responseUnit: "hours", resolutionTime: 12, resolutionUnit: "hours" },
      ],
    },
  ],
  "acme-packaging": [
    {
      id: "c7", contractNumber: "CTR-2025-0072", type: "asset", status: "active",
      assetId: "PM-2000", assetName: "Packmaster 2000",
      effectiveDate: "2025-04-01", expiryDate: "2026-03-31", paymentTerms: "Net 30",
      laborRate: 90, partsDiscountRate: 18, travelRate: 1.4, slaLevels: DEFAULT_SLA_LEVELS_IB,
    },
  ],
  "beta-solutions": [
    {
      id: "c3", contractNumber: "CTR-2024-0088", type: "company", status: "expired",
      effectiveDate: "2024-06-01", expiryDate: "2025-05-31", paymentTerms: "Due on Receipt",
      laborRate: 75, partsDiscountRate: 20, travelRate: 1.0, slaLevels: DEFAULT_SLA_LEVELS_IB,
    },
  ],
};

const COMPANY_DISPLAY_NAMES: Record<string, string> = {
  "acme-international": "Acme International",
  "acme-packaging":     "Acme Packaging Company",
  "beta-solutions":     "Beta Solutions",
  "gamma":              "Gamma Enterprises",
  "delta":              "Delta Corp",
};

const INVOICE_CONTACT_LIST: Record<string, Array<{ name: string; email: string; initials: string; color: string }>> = {
  "acme-packaging":     [
    { name: "Micheal Zeigler", email: "m.zeigler@acme-packaging.com",    initials: "MZ", color: "#9333EA" },
    { name: "Laura Kim",       email: "l.kim@acme-packaging.com",         initials: "LK", color: "#4F46E5" },
  ],
  "acme-international": [
    { name: "James Mcdonald",  email: "j.mcdonald@acme-international.com", initials: "JM", color: "#2563EB" },
    { name: "Alex Turner",     email: "a.turner@acme-international.com",   initials: "AT", color: "#0891B2" },
  ],
  "beta-solutions":     [
    { name: "Sarah Connor",    email: "s.connor@betasolutions.com",        initials: "SC", color: "#059669" },
  ],
  "gamma":              [
    { name: "Lena Huang",      email: "l.huang@gamma.com",                 initials: "LH", color: "#D97706" },
    { name: "Mark Chen",       email: "m.chen@gamma.com",                  initials: "MC", color: "#7C3AED" },
  ],
  "delta":              [
    { name: "Roy Fischer",     email: "r.fischer@deltacorp.com",            initials: "RF", color: "#EF4444" },
  ],
};

function StatusBadgeDropdown({
  status,
  onChange,
}: {
  status: InvoiceStatus;
  onChange: (s: InvoiceStatus) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const cfg = STATUS_CONFIG[status];
  const all: InvoiceStatus[] = ['draft', 'sent', 'approved', 'paid', 'overdue'];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 6px 2px 8px', borderRadius: 4, border: 'none',
          background: cfg.bg, color: cfg.color,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.3px',
          fontFamily: 'var(--font-family-base)', cursor: 'pointer',
        }}
      >
        {cfg.label}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke={cfg.color} strokeWidth="1.4" strokeLinecap="round">
          <path d="M2 3.5l3 3 3-3" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50,
          background: '#fff', border: '1px solid #E8EAED', borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.10)', overflow: 'hidden', minWidth: 120,
        }}>
          {all.map((s) => {
            const c = STATUS_CONFIG[s];
            const active = s === status;
            return (
              <button
                key={s}
                onClick={() => { onChange(s); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '7px 12px', border: 'none', background: 'none',
                  cursor: 'pointer', textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#F9FAFB')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.3px',
                  background: c.bg, color: c.color,
                  fontFamily: 'var(--font-family-base)',
                }}>
                  {c.label}
                </span>
                {active && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M2 6l3 3 5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Record Payment Modal ─────────────────────────────────────────────────────

const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "credit_card",   label: "Credit card" },
  { value: "cash",          label: "Cash" },
  { value: "check",         label: "Check" },
  { value: "other",         label: "Other" },
];

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatIsoDate(iso: string) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function fmtModalEur(n: number) {
  return `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function RecordPaymentModal({
  isOpen,
  onClose,
  onRecord,
  outstandingBalance,
}: {
  isOpen: boolean;
  onClose: () => void;
  onRecord: (p: PaymentRecord) => void;
  outstandingBalance: number;
}) {
  const [amount, setAmount]           = React.useState(0);
  const [dateReceived, setDateReceived] = React.useState(todayIso);
  const [method, setMethod]           = React.useState("bank_transfer");
  const [reference, setReference]     = React.useState("");
  const [note, setNote]               = React.useState("");

  React.useEffect(() => {
    if (isOpen) {
      setAmount(0);
      setDateReceived(todayIso());
      setMethod("bank_transfer");
      setReference("");
      setNote("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  function handleSubmit() {
    if (amount <= 0) return;
    onRecord({ id: `pay-${Date.now()}`, amount, dateReceived, method, reference, note });
    onClose();
  }

  const modalInput: React.CSSProperties = {
    width: "100%", height: 44, padding: "0 14px",
    border: "1.5px solid #E8EAED", borderRadius: 8,
    background: "#fff", fontFamily: "var(--font-family-base)",
    fontSize: 14, fontWeight: 400, color: "#172B4D",
    outline: "none", boxSizing: "border-box", appearance: "none" as const,
  };

  const modalLabel: React.CSSProperties = {
    display: "block", fontSize: 13, fontWeight: 600,
    color: "#172B4D", marginBottom: 6,
    fontFamily: "var(--font-family-base)",
  };

  const requiredStar: React.CSSProperties = {
    color: "#EF4444", marginLeft: 2,
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(23,43,77,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520,
        boxShadow: "0 24px 64px rgba(23,43,77,0.18)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "22px 24px 18px" }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.3px" }}>
            Record payment
          </span>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8, border: "1.5px solid #E8EAED",
              background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              color: "#6B7280", fontSize: 16, lineHeight: 1,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M2 2l10 10M12 2L2 12" />
            </svg>
          </button>
        </div>

        <div style={{ height: 1, background: "#E8EAED", flexShrink: 0 }} />

        {/* Body */}
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16, overflowY: "auto" }}>
          {/* Outstanding balance */}
          <div style={{
            background: "#F4F5F7", borderRadius: 8, padding: "12px 16px",
            fontFamily: "var(--font-family-base)", fontSize: 13, color: "#172B4D",
          }}>
            Outstanding balance: <strong>{fmtModalEur(outstandingBalance)}</strong>
          </div>

          {/* Amount + Date received */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <FormField
              label="Amount"
              required
              type="number"
              min={0}
              step={0.01}
              leadingBadge="EUR"
              value={String(amount)}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
            <DateField
              label="Date received"
              required
              value={dateReceived}
              onChange={(e) => setDateReceived(e.target.value)}
            />
          </div>

          {/* Payment method */}
          <SelectField
            label="Payment method"
            value={method}
            onChange={setMethod}
            options={PAYMENT_METHODS}
          />

          {/* Reference */}
          <FormField
            label="Reference / transaction ID"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. TXN-4829174"
          />

          {/* Internal note */}
          <TextareaField
            label="Internal note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note about this payment..."
            rows={3}
          />
        </div>

        {/* Footer */}
        <div style={{ height: 1, background: "#E8EAED", flexShrink: 0 }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, padding: "16px 24px" }}>
          <button
            onClick={onClose}
            style={{
              height: 40, padding: "0 20px", border: "1.5px solid #E8EAED", borderRadius: 8,
              background: "#fff", cursor: "pointer",
              fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 600, color: "#172B4D",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={amount <= 0}
            style={{
              height: 40, padding: "0 20px", border: "none", borderRadius: 8,
              background: amount > 0 ? "#0517F8" : "#E8EAED", cursor: amount > 0 ? "pointer" : "not-allowed",
              fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 700,
              color: amount > 0 ? "#fff" : "#9CA3AF", letterSpacing: "-0.1px",
            }}
          >
            Record payment
          </button>
        </div>
      </div>
    </div>
  );
}

// SendEmailModal is imported from the shared component

// ─── Customer Change Request Modal ───────────────────────────────────────────

function CustomerChangeRequestModal({
  isOpen,
  onClose,
  onSubmit,
  customerName,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (message: string) => void;
  customerName: string;
}) {
  const [message, setMessage] = React.useState("");

  React.useEffect(() => {
    if (isOpen) setMessage("");
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(23,43,77,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480,
        boxShadow: "0 24px 64px rgba(23,43,77,0.18)",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "22px 24px 18px" }}>
          <div>
            <span style={{ fontSize: 17, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.3px", display: "block" }}>
              Customer Requested Changes
            </span>
            <span style={{ fontSize: 13, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>
              From {customerName}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8, border: "1.5px solid #E8EAED",
              background: "#fff", cursor: "pointer", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M2 2l10 10M12 2L2 12" />
            </svg>
          </button>
        </div>

        <div style={{ height: 1, background: "#E8EAED" }} />

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#6B7280", fontFamily: "var(--font-family-base)", lineHeight: 1.6 }}>
            Enter the customer's feedback. It will be highlighted on the document so you can address it before re-sending.
          </p>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#172B4D", marginBottom: 6, fontFamily: "var(--font-family-base)" }}>
              Change Request Notes
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Line item 1 quantity should be 3, and please revise the payment terms..."
              rows={5}
              autoFocus
              style={{
                width: "100%", padding: "12px 14px",
                border: "1.5px solid #E8EAED", borderRadius: 8,
                background: "#fff", fontFamily: "var(--font-family-base)",
                fontSize: 14, color: "#172B4D", outline: "none",
                boxSizing: "border-box", resize: "vertical" as const, minHeight: 120, lineHeight: 1.65,
              }}
            />
          </div>
        </div>

        <div style={{ height: 1, background: "#E8EAED" }} />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, padding: "16px 24px" }}>
          <button
            onClick={onClose}
            style={{
              height: 40, padding: "0 20px", border: "1.5px solid #E8EAED", borderRadius: 8,
              background: "#fff", cursor: "pointer",
              fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 600, color: "#172B4D",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => { if (message.trim()) { onSubmit(message.trim()); onClose(); } }}
            disabled={!message.trim()}
            style={{
              height: 40, padding: "0 20px", border: "none", borderRadius: 8,
              background: message.trim() ? "#EA580C" : "#E8EAED",
              cursor: message.trim() ? "pointer" : "not-allowed",
              fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 700,
              color: message.trim() ? "#fff" : "#9CA3AF",
            }}
          >
            Save Change Request
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Invoice Portal Overlay ───────────────────────────────────────────────────

function InvoicePortalOverlay({
  invoiceNumber,
  issueDate,
  dueDate,
  contact,
  lineItems,
  subTotal,
  marginTotal,
  discountTotal,
  grandTotal,
  discountPct,
  marginPct,
  note,
  onClose,
  onRequestChanges,
}: {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  contact: { name: string; email: string; company: string; initials: string; color: string };
  lineItems: LineItem[];
  subTotal: number;
  marginTotal: number;
  discountTotal: number;
  grandTotal: number;
  discountPct: number;
  marginPct: number;
  note: string;
  onClose: () => void;
  onRequestChanges: (message: string) => void;
}) {
  const { config: financeConfig } = useFinanceConfig();
  const [showChangeForm, setShowChangeForm] = React.useState(false);
  const [changeMsg, setChangeMsg] = React.useState("");

  const fmtEur = (n: number) =>
    `€ ${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", background: "#F1F5F9" }}>
      {/* Preview bar */}
      <div style={{ flexShrink: 0, background: "#111827", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", height: 48 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#F59E0B" }} />
          <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "var(--font-family-base)", color: "#F9FAFB" }}>
            Customer Portal Preview
          </span>
          <span style={{ fontSize: 12, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>
            — This is how {contact.name.split(" ")[0]} will see the invoice
          </span>
        </div>
        <button
          onClick={onClose}
          style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #374151", borderRadius: 6, background: "none", cursor: "pointer", padding: "6px 12px", color: "#D1D5DB", fontSize: 13, fontFamily: "var(--font-family-base)", fontWeight: 500 }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M2 2l8 8M10 2L2 10" />
          </svg>
          Close Preview
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "40px 24px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Invoice card */}
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E5E7EB", overflow: "hidden", boxShadow: "0 4px 24px rgba(23,43,77,0.10)" }}>

            {/* Company Header Image Zone */}
            {financeConfig.headerImage ? (
              <div style={{ overflow: "hidden", borderBottom: "1px solid #D4D8FF" }}>
                <img src={financeConfig.headerImage} alt="Header" style={{ width: "100%", display: "block", maxHeight: 120, objectFit: "cover" }} />
              </div>
            ) : (
              <div style={{
                position: "relative",
                background: "linear-gradient(135deg, #E6E8FE 0%, #EEF0FF 60%, #F5F6FF 100%)",
                borderBottom: "1px solid #D4D8FF",
                minHeight: 108,
                overflow: "hidden",
              }}>
                <div style={{ position: "absolute", right: -30, top: -30, width: 140, height: 140, borderRadius: "50%", background: "rgba(5,23,248,0.07)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", right: 80, bottom: -20, width: 80, height: 80, borderRadius: "50%", background: "rgba(5,23,248,0.04)", pointerEvents: "none" }} />
              </div>
            )}

            {/* From / Sent to / Details — 3-column info row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr 1px 1fr", borderBottom: "1px solid #E8EAED" }}>
              {/* From */}
              <div style={{ padding: "24px 28px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.8px", color: "#A2AAB8", fontFamily: "var(--font-family-base)", marginBottom: 10 }}>FROM</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", marginBottom: 6 }}>{financeConfig.senderName}</div>
                <div style={{ fontSize: 12, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: 1.7 }}>
                  {financeConfig.street}<br />{financeConfig.postalCode} {financeConfig.city}, {financeConfig.country}
                </div>
                {financeConfig.vatId && <div style={{ fontSize: 12, color: "#748094", fontFamily: "var(--font-family-base)", marginTop: 6 }}>VAT ID: {financeConfig.vatId}</div>}
              </div>
              <div style={{ background: "#E8EAED" }} />
              {/* Billed to */}
              <div style={{ padding: "24px 28px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.8px", color: "#A2AAB8", fontFamily: "var(--font-family-base)", marginBottom: 10 }}>BILLED TO</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", marginBottom: 6 }}>{contact.company}</div>
                <div style={{ fontSize: 12, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: 1.7 }}>
                  Bahnhofstraße 42<br />80335 Munich, Germany
                </div>
                <div style={{ fontSize: 12, color: "#748094", fontFamily: "var(--font-family-base)", marginTop: 6 }}>{contact.email}</div>
                <div style={{ fontSize: 12, color: "#748094", fontFamily: "var(--font-family-base)", marginTop: 2 }}>+49 89 456 789</div>
              </div>
              <div style={{ background: "#E8EAED" }} />
              {/* Document details */}
              <div style={{ padding: "24px 28px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.8px", color: "#A2AAB8", fontFamily: "var(--font-family-base)", marginBottom: 10 }}>INVOICE</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.5px", marginBottom: 14 }}>{invoiceNumber}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", color: "#A2AAB8", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>ISSUE DATE</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)" }}>{issueDate}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", color: "#A2AAB8", fontFamily: "var(--font-family-base)", marginBottom: 2 }}>DUE DATE</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)" }}>{dueDate}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Line items */}
            <div style={{ padding: "24px 32px", borderBottom: "1px solid #F3F4F6" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", fontFamily: "var(--font-family-base)", letterSpacing: "0.5px", marginBottom: 16 }}>LINE ITEMS</div>
              <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px 90px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", padding: "8px 16px" }}>
                  {["Item / Service", "Qty", "Unit Price", "Total"].map((h) => (
                    <div key={h} style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>{h}</div>
                  ))}
                </div>
                {lineItems.filter(li => li.name || li.qty > 0).map((li) => {
                  const rowTotal = li.qty * li.price * (1 + li.margin / 100) * (1 - li.discount / 100);
                  return (
                    <div key={li.id} style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px 90px", padding: "12px 16px", borderBottom: "1px solid #F3F4F6", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#111827", fontFamily: "var(--font-family-base)" }}>{li.name || "—"}</div>
                        {li.margin > 0 && (
                          <div style={{ fontSize: 11, color: "#6366F1", fontFamily: "var(--font-family-base)", marginTop: 2 }}>{li.margin}% margin</div>
                        )}
                        {li.discount > 0 && (
                          <div style={{ fontSize: 11, color: "#059669", fontFamily: "var(--font-family-base)", marginTop: 2 }}>{li.discount}% discount</div>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: "#374151", fontFamily: "var(--font-family-base)" }}>{li.qty}</div>
                      <div style={{ fontSize: 13, color: "#374151", fontFamily: "var(--font-family-base)" }}>{fmtEur(li.price)}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)" }}>{fmtEur(rowTotal)}</div>
                    </div>
                  );
                })}
                <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", width: 220 }}>
                    <span style={{ fontSize: 13, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>Sub Total</span>
                    <span style={{ fontSize: 13, color: "#374151", fontFamily: "var(--font-family-base)" }}>{fmtEur(subTotal)}</span>
                  </div>
                  {marginTotal > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", width: 220 }}>
                      <span style={{ fontSize: 13, color: "#6366F1", fontFamily: "var(--font-family-base)" }}>Margin (+{marginPct}%)</span>
                      <span style={{ fontSize: 13, color: "#6366F1", fontFamily: "var(--font-family-base)" }}>+{fmtEur(marginTotal)}</span>
                    </div>
                  )}
                  {discountTotal > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", width: 220 }}>
                      <span style={{ fontSize: 13, color: "#059669", fontFamily: "var(--font-family-base)" }}>Discount ({discountPct}%)</span>
                      <span style={{ fontSize: 13, color: "#059669", fontFamily: "var(--font-family-base)" }}>−{fmtEur(discountTotal)}</span>
                    </div>
                  )}
                  <div style={{ width: 220, borderTop: "1px solid #E5E7EB", marginTop: 4, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)" }}>Total</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)" }}>{fmtEur(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Note */}
            {note && (
              <div style={{ padding: "20px 32px", borderBottom: "1px solid #F3F4F6" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", fontFamily: "var(--font-family-base)", letterSpacing: "0.5px", marginBottom: 8 }}>NOTES</div>
                <p style={{ margin: 0, fontSize: 14, color: "#374151", fontFamily: "var(--font-family-base)", lineHeight: 1.65 }}>{note}</p>
              </div>
            )}

            {/* Company Footer Image Zone */}
            {financeConfig.footerImage ? (
              <div style={{ overflow: "hidden", borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
                <img src={financeConfig.footerImage} alt="Footer" style={{ width: "100%", display: "block", maxHeight: 120, objectFit: "cover" }} />
              </div>
            ) : (
              <div style={{
                background: "#F9FAFB",
                padding: "20px 32px",
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", marginBottom: 3 }}>
                  {financeConfig.senderName} · {financeConfig.street}, {financeConfig.postalCode} {financeConfig.city}, {financeConfig.country}
                </div>
                <div style={{ fontSize: 11, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: 1.5 }}>
                  {[
                    financeConfig.vatId && `VAT ID: ${financeConfig.vatId}`,
                    financeConfig.iban && `IBAN: ${financeConfig.iban}`,
                    financeConfig.bic && `BIC: ${financeConfig.bic}`,
                  ].filter(Boolean).join(" · ")}
                </div>
              </div>
            )}
          </div>

          {/* Action area */}
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E5E7EB", padding: "24px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, boxShadow: "0 2px 12px rgba(23,43,77,0.07)" }}>
            <button
              style={{ display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 18px", border: "1px solid #E5E7EB", borderRadius: 8, background: "#fff", cursor: "pointer", fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 600, color: "#374151" }}
            >
              <PdfIcon />
              Download PDF
            </button>
            <button
              onClick={() => setShowChangeForm(true)}
              style={{ height: 40, padding: "0 20px", border: "1.5px solid #E5E7EB", borderRadius: 8, background: "#fff", cursor: "pointer", fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 600, color: "#374151" }}
            >
              Request Changes
            </button>
          </div>

        </div>
      </div>

      {/* Change Request modal */}
      {showChangeForm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(23,43,77,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, boxShadow: "0 24px 64px rgba(23,43,77,0.20)", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "22px 24px 18px" }}>
              <div>
                <span style={{ fontSize: 17, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.3px", display: "block" }}>Request Changes</span>
                <span style={{ fontSize: 13, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>Let us know what you'd like updated</span>
              </div>
              <button onClick={() => setShowChangeForm(false)} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid #E8EAED", background: "#fff", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M2 2l10 10M12 2L2 12" /></svg>
              </button>
            </div>
            <div style={{ height: 1, background: "#E8EAED" }} />
            <div style={{ padding: "20px 24px" }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#172B4D", marginBottom: 8, fontFamily: "var(--font-family-base)" }}>
                Describe the changes you'd like
              </label>
              <textarea
                value={changeMsg}
                onChange={(e) => setChangeMsg(e.target.value)}
                placeholder="e.g. Please update the quantity for item 2, and revise the payment terms to Net 30..."
                rows={5}
                autoFocus
                style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #E8EAED", borderRadius: 8, background: "#fff", fontFamily: "var(--font-family-base)", fontSize: 14, color: "#172B4D", outline: "none", boxSizing: "border-box", resize: "vertical" as const, lineHeight: 1.65 }}
              />
            </div>
            <div style={{ height: 1, background: "#E8EAED" }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, padding: "16px 24px" }}>
              <button onClick={() => setShowChangeForm(false)} style={{ height: 40, padding: "0 20px", border: "1.5px solid #E8EAED", borderRadius: 8, background: "#fff", cursor: "pointer", fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 600, color: "#172B4D" }}>
                Cancel
              </button>
              <button
                onClick={() => { if (changeMsg.trim()) { onRequestChanges(changeMsg.trim()); setShowChangeForm(false); onClose(); } }}
                disabled={!changeMsg.trim()}
                style={{ height: 40, padding: "0 20px", border: "none", borderRadius: 8, background: changeMsg.trim() ? "#EA580C" : "#E8EAED", cursor: changeMsg.trim() ? "pointer" : "not-allowed", fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 700, color: changeMsg.trim() ? "#fff" : "#9CA3AF" }}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Contract panel helpers ───────────────────────────────────────────────────

// ─── Main Page ────────────────────────────────────────────────────────────────

export function InvoiceBuilderPage({ invoiceNumber = "IN-2026-0009", onBack, poNumber, poDocument, initialStatus = 'sent', linkedWo, onNavigateToWorkOrder, initialLineItems, onEmailSent }: InvoiceBuilderPageProps) {
  const { config: financeConfig } = useFinanceConfig();

  const [invoiceNum, setInvoiceNum] = React.useState(invoiceNumber);
  const [quoteNumber, setQuoteNumber] = React.useState("G-000123");
  const [issueDate, setIssueDate] = React.useState(() => todayFormatted());
  const [dueDate, setDueDate] = React.useState(() => todayFormatted());
  const [billingAddress, setBillingAddress] = React.useState("Am Kölner Hof 13, 50679 Köln, Germany");
  const [vatId, setVatId] = React.useState(() => financeConfig.vatId);
  const [paymentInfo, setPaymentInfo] = React.useState(() => {
    const parts: string[] = [];
    if (financeConfig.iban) parts.push(`IBAN: ${financeConfig.iban}`);
    if (financeConfig.bic) parts.push(`BIC: ${financeConfig.bic}`);
    return parts.length ? parts.join("\n") : "IBAN: DE89 3704 0044 0532 0130 00\nBIC: COBADEFFXXX\nBank: Commerzbank AG";
  });
  const [note, setNote] = React.useState(() => financeConfig.invoiceNotes || "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.");
  const [detailsCollapsed, setDetailsCollapsed] = React.useState(false);
  const [selectedPriceBookId, setSelectedPriceBookId] = React.useState<string>("");
  const [company] = React.useState("acme-international");
  const [contractCollapsed, setContractCollapsed] = React.useState(false);
  const [activityLogCollapsed, setActivityLogCollapsed] = React.useState(false);
  const [selectedContractPanel, setSelectedContractPanel] = React.useState<Contract | null>(null);
  const activeContracts = COMPANY_CONTRACTS[company] ?? [];
  const activeContract = activeContracts.find(c => c.type === "asset") ?? activeContracts.find(c => c.type === "company") ?? null;

  const nowStamp = () => {
    const now = new Date();
    return now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' at ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  type ActivityType = 'created' | 'sent' | 'approved' | 'changes_requested' | 'note';
  interface InvoiceActivity { id: string; type: ActivityType; message: string; timestamp: string; user?: string; }

  const [activityLog, setActivityLog] = React.useState<InvoiceActivity[]>([
    { id: 'act-init', type: 'created', message: 'Invoice created', timestamp: '12 Jun 2026 at 09:45', user: 'James Mcdonald' },
  ]);

  function addActivity(type: ActivityType, message: string, user?: string) {
    setActivityLog((prev) => [{
      id: `act-${Date.now()}`,
      type,
      message,
      timestamp: nowStamp(),
      user,
    }, ...prev]);
  }

  React.useEffect(() => {
    if (!activeContract) return;
    setPaymentInfo((prev) => {
      if (prev.startsWith("Payment Terms:")) return prev;
      return `Payment Terms: ${activeContract.paymentTerms}\n${prev}`;
    });
    const days = parsePaymentDays(activeContract.paymentTerms);
    if (days > 0) setDueDate(addDaysToFormatted(todayFormatted(), days));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [lineItems, setLineItems] = React.useState<LineItem[]>(() =>
    initialLineItems && initialLineItems.length > 0
      ? initialLineItems
      : [
          { id: "li-1", name: "TurboMax Engine Oil 4L - 000123", qty: 1, unit: "each", price: 400, discount: 0, margin: financeConfig.defaultMargin ?? 0 },
          { id: "li-2", name: "Technician Charges", qty: 5, unit: "hour", price: 20, discount: 0, margin: financeConfig.defaultMargin ?? 0 },
        ]
  );

  const [status, setStatus] = React.useState<InvoiceStatus>(initialStatus);
  const [changeRequest, setChangeRequest] = React.useState<ChangeRequestInfo | null>(null);
  const [approvalInfo, setApprovalInfo] = React.useState<ApprovalInfo | null>(null);
  const [panelPoFile, setPanelPoFile] = React.useState<File | null>(poDocument ?? null);
  const panelPoInputRef = React.useRef<HTMLInputElement>(null);
  const [showChangeRequestModal, setShowChangeRequestModal] = React.useState(false);
  const [paymentRecords, setPaymentRecords] = React.useState<PaymentRecord[]>([]);
  const [showRecordModal, setShowRecordModal] = React.useState(false);
  const [showEmailModal, setShowEmailModal] = React.useState(false);
  const [showCatalogModal, setShowCatalogModal] = React.useState(false);
  const [showPortalPreview, setShowPortalPreview] = React.useState(false);

  function handleAddCatalogItem(item: CatalogItem) {
    setLineItems((prev) => [
      ...prev,
      {
        id: `li-${item.type}-${Date.now()}`,
        catalogId: item.id,
        name: item.name,
        qty: 1,
        unit: item.unit,
        price:
          item.type === "labor"  ? (activeContract?.laborRate  ?? 0) :
          item.type === "travel" ? (activeContract?.travelRate ?? 0) :
          item.basePrice,
        discount: item.type === "part" ? (activeContract?.partsDiscountRate ?? 0) : 0,
        margin: financeConfig.defaultMargin ?? 0,
      },
    ]);
  }

  const QUOTE_OPTIONS = [
    { value: "G-000123", label: "G-000123" },
    { value: "QT-2026-0001", label: "QT-2026-0001" },
    { value: "QT-2026-0002", label: "QT-2026-0002" },
  ];

  function addRow() {
    setLineItems((prev) => [...prev, { id: `li-${Date.now()}`, name: "", qty: 0, unit: "each", price: 0, discount: activeContract ? activeContract.partsDiscountRate : 0, margin: financeConfig.defaultMargin ?? 0 }]);
  }

  function updateRow(id: string, field: keyof LineItem, value: string | number) {
    setLineItems((prev) =>
      prev.map((li) => {
        if (li.id !== id) return li;
        const updated = { ...li, [field]: value };
        if (field === "unit" && value === "hour" && updated.price === 0 && activeContract) {
          updated.price = activeContract.laborRate;
        }
        return updated;
      })
    );
  }

  function deleteRow(id: string) {
    setLineItems((prev) => prev.filter((li) => li.id !== id));
  }

  const getRowTotal = (li: LineItem) => li.qty * li.price * (1 + li.margin / 100) * (1 - li.discount / 100);
  const subTotal      = lineItems.reduce((s, li) => s + li.qty * li.price, 0);
  const marginTotal   = lineItems.reduce((s, li) => s + li.qty * li.price * (li.margin / 100), 0);
  const discountTotal = lineItems.reduce((s, li) => s + li.qty * li.price * (li.discount / 100), 0);
  const grandTotal    = lineItems.reduce((s, li) => s + getRowTotal(li), 0);
  const discountPct = subTotal > 0 ? Math.round((discountTotal / subTotal) * 100) : 0;
  const marginPct   = subTotal > 0 ? Math.round((marginTotal  / subTotal) * 100) : 0;
  const fmtEur = (n: number) => `€ ${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const totalPaid = paymentRecords.reduce((s, p) => s + p.amount, 0);
  const outstandingBalance = Math.max(0, grandTotal - totalPaid);

  function handleRecordPayment(p: PaymentRecord) {
    setPaymentRecords((prev) => [...prev, p]);
    if (outstandingBalance - p.amount <= 0) setStatus('paid');
  }

  function handleSendEmail() {
    setStatus('sent');
    setChangeRequest(null);
    addActivity('sent', 'Invoice emailed to Micheal Zeigler', 'James Mcdonald');
    const contactList = INVOICE_CONTACT_LIST[company] ?? INVOICE_CONTACT_LIST["acme-international"];
    const primaryContact = contactList[0];
    onEmailSent?.({
      docType: "Invoice",
      docNumber: invoiceNum,
      fromEmail: MOCK_SENDER_ACCOUNTS[0].email,
      toEmail: primaryContact.email,
      toName: primaryContact.name,
      subject: `Invoice ${invoiceNum}`,
      body: `Dear ${primaryContact.name.split(" ")[0]},\n\nPlease find attached Invoice ${invoiceNum} for Work Order ${linkedWo?.number ?? ""}.\n\nKind regards,\nJames McDonald`,
    });
  }

  function handleSubmitChangeRequest(message: string) {
    const ts = nowStamp();
    setChangeRequest({
      message,
      customerName: "Micheal Zeigler",
      timestamp: ts,
    });
    setStatus('changes_requested');
    addActivity('changes_requested', 'Micheal Zeigler requested changes');
  }

  function handleApprove() {
    const now = new Date();
    setApprovalInfo({
      approvedAt: now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      approvedBy: "Micheal Zeigler",
    });
    setChangeRequest(null);
    setStatus('approved');
    addActivity('approved', 'Invoice approved by Micheal Zeigler');
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "#fff" }}>

      <PageHeader
        breadcrumbs={[{ label: "Invoices", onClick: onBack }, { label: invoiceNum }]}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-s)" }}>
            <button style={{
              height: 40, padding: "0 var(--spacing-s)",
              border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
              background: "var(--color-bg-primary)", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "var(--spacing-2xs)",
              fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
              letterSpacing: "-0.24px", color: "var(--color-content-primary)",
            }}>
              <PdfIcon />
              PDF
            </button>
            <button style={{
              height: 40, padding: "0 var(--spacing-s)",
              border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
              background: "var(--color-bg-primary)", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "var(--spacing-2xs)",
              fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
              letterSpacing: "-0.24px", color: "var(--color-content-primary)",
            }}>
              <DuplicateIcon />
              Duplicate
            </button>
            <button
              onClick={() => setShowPortalPreview(true)}
              style={{
                height: 40, padding: "0 var(--spacing-s)",
                border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
                background: "var(--color-bg-primary)", cursor: "pointer",
                display: "flex", alignItems: "center", gap: "var(--spacing-2xs)",
                fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
                letterSpacing: "-0.24px", color: "var(--color-content-primary)",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="7" cy="7" r="5.5" />
                <circle cx="7" cy="7" r="2" />
              </svg>
              View as Customer
            </button>
            <button style={{
              width: 40, height: 40,
              border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
              background: "var(--color-bg-primary)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--color-content-secondary)",
            }}>
              <MoreIcon />
            </button>
            {status === 'changes_requested' ? (
              <button
                onClick={() => setShowEmailModal(true)}
                style={{
                  height: 40, padding: "0 var(--spacing-s)",
                  border: "none", borderRadius: "var(--radius-s)",
                  background: "var(--color-bg-warning)", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "var(--spacing-2xs)",
                  fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
                  letterSpacing: "-0.24px", color: "var(--color-content-inverse)",
                }}
              >
                <SendIcon />
                Re-send for Approval
              </button>
            ) : (
              <button
                disabled={status === 'paid'}
                onClick={() => setShowEmailModal(true)}
                style={{
                  height: 40, padding: "0 var(--spacing-s)",
                  border: "none", borderRadius: "var(--radius-s)",
                  background: status === 'paid' ? "#E5E7EB" : "var(--color-bg-brand-dark)",
                  cursor: status === 'paid' ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: "var(--spacing-2xs)",
                  fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
                  letterSpacing: "-0.24px", color: status === 'paid' ? "#9CA3AF" : "var(--color-content-inverse)",
                  opacity: status === 'paid' ? 0.7 : 1,
                }}
              >
                <SendIcon />
                Send
              </button>
            )}
            <button style={{
              height: 40, padding: "0 var(--spacing-s)",
              border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
              background: "var(--color-bg-accent)", cursor: "not-allowed",
              fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
              letterSpacing: "-0.24px", color: "var(--color-content-secondary)",
            }}>
              Save Draft
            </button>
          </div>
        }
      />

      {/* ── Two-column body ───────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* ── Left: main content ────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: "auto", borderRight: "1px solid #E5E7EB" }}>

          {/* ── Change Request Banner ─────────────────────────────────────── */}
          {status === 'changes_requested' && changeRequest && (
            <div style={{
              margin: "20px 32px 0",
              padding: "14px 16px",
              background: "#FFF7ED",
              border: "1px solid #FDBA74",
              borderRadius: 8,
              display: "flex",
              gap: 12,
            }}>
              <div style={{ flexShrink: 0, marginTop: 2 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#EA580C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="8" cy="8" r="6.5" />
                  <path d="M8 5.5v3M8 10.5v.5" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#C2410C", fontFamily: "var(--font-family-base)" }}>
                    Changes requested by {changeRequest.customerName}
                  </span>
                  <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "var(--font-family-base)", flexShrink: 0, marginLeft: 12 }}>
                    {changeRequest.timestamp}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: "#374151", fontFamily: "var(--font-family-base)", lineHeight: 1.6 }}>
                  {changeRequest.message}
                </p>
              </div>
            </div>
          )}

          {/* ── Approval Banner ───────────────────────────────────────────── */}
          {status === 'approved' && approvalInfo && (
            <div style={{
              margin: "20px 32px 0",
              padding: "12px 16px",
              background: "#F0FDF4",
              border: "1px solid #86EFAC",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="8" r="6.5" />
                <path d="M5 8l2.5 2.5 4-4" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#15803D", fontFamily: "var(--font-family-base)" }}>
                Approved by {approvalInfo.approvedBy}
              </span>
              <span style={{ fontSize: 12, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>
                · {approvalInfo.approvedAt}
              </span>
            </div>
          )}

          {/* ── Invoice Details ──────────────────────────────────────────── */}
          <div style={{ padding: "28px 32px", borderBottom: "1px solid #E5E7EB" }}>
            <h2 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.3px" }}>
              Invoice Details
            </h2>

            {/* Row 1: Invoice Number */}
            <div style={{ marginBottom: 16 }}>
              <FormField
                label="Invoice Number"
                value={invoiceNum}
                onChange={(e) => setInvoiceNum(e.target.value)}
              />
            </div>

            {/* Row 2: Created Date + Due Date */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", marginBottom: 16 }}>
              <DateField
                label="Created Date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
              />
              <DateField
                label="Due Date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            {/* Row 3: Billing Address + VAT ID */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", marginBottom: 16 }}>
              <FormField
                label="Billing Address"
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
              />
              <FormField
                label="VAT ID"
                value={vatId}
                onChange={(e) => setVatId(e.target.value)}
                placeholder="e.g. DE123456789"
              />
            </div>

            {/* Row 4: Price Book */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
              <SelectField
                label="Price Book"
                value={selectedPriceBookId}
                onChange={setSelectedPriceBookId}
                options={financeConfig.priceBooks.map((pb: PriceBook) => ({ value: pb.id, label: `${pb.name} (${pb.currency})` }))}
              />
            </div>
          </div>

          {/* ── Line Items ───────────────────────────────────────────────── */}
          <div style={{ padding: "28px 32px", borderBottom: "1px solid #E5E7EB" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.3px" }}>
                Line Items
              </h2>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setShowCatalogModal(true)}
                  style={{
                    height: 32, padding: "0 12px", border: "1px solid #E5E7EB", borderRadius: 6,
                    background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                    fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 500, color: "#374151",
                  }}>
                  <PlusIcon />
                  Browse Items
                </button>
                <button onClick={addRow} style={{
                  height: 32, padding: "0 12px", border: "1px solid #E5E7EB", borderRadius: 6,
                  background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 500, color: "#374151",
                }}>
                  <PlusIcon />
                  Add Row
                </button>
              </div>
            </div>

            <div style={{ border: "1px solid #E5E7EB", borderRadius: 6, overflow: "hidden" }}>
              {/* Column headers */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 68px 108px 104px 88px 88px 88px 36px",
                background: "#F9FAFB", borderBottom: "1px solid #E5E7EB",
                height: 34, alignItems: "center",
              }}>
                {["Item / Service", "Qty", "Unit", "Price", "Discount", "Margin", "Total"].map((label) => (
                  <div key={label} style={{ padding: "0 10px", fontSize: 12, fontWeight: 500, color: "#6B7280", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
                    {label}
                  </div>
                ))}
                <div />
              </div>

              {lineItems.map((li) => (
                <LineItemRow key={li.id} item={li} onUpdate={updateRow} onDelete={deleteRow} rowTotal={getRowTotal(li)} />
              ))}

              {/* Totals */}
              <div style={{ borderTop: "1px solid #E5E7EB", padding: "10px 12px 12px", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <TotalRow label="Sub Total" value={fmtEur(subTotal)} bold={false} />
                {marginTotal > 0 && <TotalRow label={`Margin (+${marginPct}%)`} value={`+${fmtEur(marginTotal)}`} bold={false} />}
                {discountTotal > 0 && <TotalRow label={`Discount (${discountPct}%)`} value={`−${fmtEur(discountTotal)}`} bold={false} />}
                <div style={{ width: "100%", borderTop: "1px solid #E5E7EB", margin: "4px 0" }} />
                <TotalRow label="Total" value={fmtEur(grandTotal)} bold={true} />
              </div>
            </div>
          </div>

          {/* ── Notes & Terms ────────────────────────────────────────────── */}
          <div style={{ padding: "28px 32px" }}>
            <h2 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.3px" }}>
              Notes & Terms
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <div style={fieldWrap}>
                <label style={labelStyle}>Payment Information</label>
                <textarea
                  value={paymentInfo}
                  onChange={(e) => setPaymentInfo(e.target.value)}
                  rows={4}
                  style={{ ...inputBase, height: "auto", padding: "var(--spacing-m)", resize: "vertical", minHeight: 90, lineHeight: 1.5 }}
                />
              </div>
              <div style={fieldWrap}>
                <label style={labelStyle}>Note</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  style={{ ...inputBase, height: "auto", padding: "var(--spacing-m)", resize: "vertical", minHeight: 90, lineHeight: 1.5 }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Right sidebar ─────────────────────────────────────────────── */}
        <div style={{ width: 340, flexShrink: 0, overflowY: "auto", background: "#fff" }}>

          {/* Details panel */}
          <div style={{ borderBottom: "1px solid #E5E7EB" }}>
            <div
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", cursor: "pointer" }}
              onClick={() => setDetailsCollapsed(!detailsCollapsed)}
            >
              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
                Details
              </span>
              {detailsCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </div>

            {!detailsCollapsed && (
              <div style={{ padding: "4px 20px 20px" }}>
                <DetailRow icon={<CalendarDetailIcon />} label="Created at" value="12/06/2026" />
                <DetailRow
                  icon={<UserIcon />} label="Created by"
                  value={
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Avatar initials="JM" color="#2563EB" />
                      <span style={{ fontSize: 13, color: "#111827", fontFamily: "var(--font-family-base)", fontWeight: 500 }}>James Mcdonald</span>
                    </div>
                  }
                />
                <DetailRow
                  icon={<StatusIcon />} label="Status"
                  value={
                    <StatusBadgeDropdown
                      status={status}
                      onChange={(s) => {
                        if (s === 'sent') {
                          setShowEmailModal(true);
                        } else if (s === 'paid') {
                          setShowRecordModal(true);
                        } else if (s === 'changes_requested') {
                          setShowChangeRequestModal(true);
                        } else if (s === 'approved') {
                          handleApprove();
                        } else {
                          setStatus(s);
                        }
                      }}
                    />
                  }
                />
                {poNumber && (
                  <DetailRow icon={<HashIcon />} label="PO Number" value={poNumber} />
                )}
                <DetailRow icon={<CompanyIcon />} label="Company" value="Acme International" />
                <DetailRow
                  icon={<ContactIcon />} label="Contact"
                  value={
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Avatar initials="MZ" color="#9333EA" />
                      <span style={{ fontSize: 13, color: "#111827", fontFamily: "var(--font-family-base)", fontWeight: 500 }}>Micheal Zeigler</span>
                    </div>
                  }
                />
              </div>
            )}
          </div>

          {/* Linked Quote panel */}
          {quoteNumber && (
            <div style={{ borderBottom: "1px solid #E5E7EB", padding: "16px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                <QuoteDocIcon color="#9CA3AF" />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
                  Linked Quote
                </span>
              </div>
              <div style={{
                border: "1px solid #E5E7EB", borderRadius: 8,
                padding: "10px 12px", display: "flex", alignItems: "center", gap: 10,
                cursor: "pointer", transition: "background 80ms ease",
              }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: 6, background: "#e6e8fe",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <QuoteDocIcon color="#6974fb" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", lineHeight: "18px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {quoteNumber}
                  </div>
                  <div style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
                    12/06/2026
                  </div>
                </div>
                <span style={{
                  padding: "3px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                  background: "#e6f9f1", color: "#10b981",
                  fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
                }}>
                  APPROVED
                </span>
              </div>
            </div>
          )}

          {/* Work Order panel */}
          <div style={{ borderBottom: "1px solid #E5E7EB", padding: "16px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: linkedWo ? 12 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <WorkOrderIcon />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
                  Work Order
                </span>
              </div>
              {!linkedWo && (
                <button style={{ border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#2563EB", fontFamily: "var(--font-family-base)", padding: 0 }}>
                  Add
                </button>
              )}
            </div>
            {linkedWo && (
              <div
                onClick={() => onNavigateToWorkOrder?.(linkedWo.number)}
                style={{
                  border: "1px solid #E5E7EB", borderRadius: 6, padding: "10px 12px",
                  display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                  transition: "background 80ms ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ width: 28, height: 28, borderRadius: 6, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <WorkOrderIcon />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", lineHeight: "18px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {linkedWo.title}
                  </div>
                  <div style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
                    {linkedWo.number}
                  </div>
                </div>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5.5 3.5L9 7l-3.5 3.5" />
                </svg>
              </div>
            )}
          </div>

          {/* Purchase Order panel */}
          {(() => {
            const effectiveDoc = panelPoFile;
            const effectiveNum = poNumber ?? "";
            const openDoc = () => {
              if (effectiveDoc) {
                const url = URL.createObjectURL(effectiveDoc);
                window.open(url, "_blank");
              }
            };
            return (
              <div style={{ borderBottom: "1px solid #E5E7EB", padding: "16px 20px" }}>
                <input
                  ref={panelPoInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  style={{ display: "none" }}
                  onChange={e => { setPanelPoFile(e.target.files?.[0] ?? null); e.target.value = ""; }}
                />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
                      <path d="M4.5 4.5h5M4.5 7h5M4.5 9.5h3" />
                    </svg>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
                      Purchase Order
                    </span>
                    {effectiveNum && (
                      <span style={{ fontSize: 11, fontWeight: 700, background: "#D1FAE5", color: "#065F46", borderRadius: 4, padding: "1px 6px", fontFamily: "var(--font-family-base)" }}>
                        Approved
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => panelPoInputRef.current?.click()}
                    style={{ fontSize: 12, fontWeight: 600, color: "var(--color-content-brand)", fontFamily: "var(--font-family-base)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  >
                    Attach
                  </button>
                </div>
                {effectiveDoc || effectiveNum ? (
                  <div
                    onClick={openDoc}
                    style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8, cursor: effectiveDoc ? "pointer" : "default", transition: "background 80ms ease" }}
                    onMouseEnter={e => { if (effectiveDoc) e.currentTarget.style.background = "#F9FAFB"; }}
                    onMouseLeave={e => { if (effectiveDoc) e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
                        <path d="M4.5 4.5h5M4.5 7h5M4.5 9.5h3" />
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", lineHeight: "18px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {effectiveNum ? `PO ${effectiveNum}` : effectiveDoc?.name ?? "Purchase Order"}
                      </div>
                      <div style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
                        {effectiveDoc ? (effectiveNum ? effectiveDoc.name : "Click to view") : "No attachment"}
                      </div>
                    </div>
                    {effectiveDoc && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5.5 3.5L9 7l-3.5 3.5" />
                      </svg>
                    )}
                  </div>
                ) : (
                  <div
                    onClick={() => panelPoInputRef.current?.click()}
                    style={{ border: "1px dashed #E5E7EB", borderRadius: 6, padding: "12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", transition: "background 80ms ease" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#F9FAFB"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
                        <path d="M4.5 4.5h5M4.5 7h5M4.5 9.5h3" />
                      </svg>
                    </div>
                    <span style={{ fontSize: 12, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>No PO attached — click to attach</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Contract panel */}
          <div style={{ borderBottom: "1px solid #E5E7EB", padding: "16px 20px" }}>
            <div
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", marginBottom: contractCollapsed ? 0 : 12 }}
              onClick={() => setContractCollapsed((c) => !c)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <ContractIcon />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
                  Contracts
                </span>
                {activeContracts.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, background: "#E6E8FE", color: "#0517F8", borderRadius: 4, padding: "1px 6px", fontFamily: "var(--font-family-base)" }}>
                    {activeContracts.length}
                  </span>
                )}
              </div>
              {contractCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </div>
            {!contractCollapsed && (
              activeContracts.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {activeContracts.map((c) => (
                    <ContractCard key={c.id} contract={c} companyName={COMPANY_DISPLAY_NAMES[company]} onClick={() => setSelectedContractPanel(c)} />
                  ))}
                </div>
              ) : (
                <div style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "16px 12px", textAlign: "center", fontSize: 12, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>
                  No contracts for this company.
                </div>
              )
            )}
          </div>

          {/* Activity Log panel */}
          <div style={{ borderBottom: "1px solid #E5E7EB", padding: "16px 20px" }}>
            <div
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", marginBottom: activityLogCollapsed ? 0 : 14 }}
              onClick={() => setActivityLogCollapsed((c) => !c)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1.5" y="1.5" width="11" height="11" rx="2" />
                  <path d="M4.5 7h5M4.5 4.5h3M4.5 9.5h2" />
                </svg>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>Activity Log</span>
              </div>
              {activityLogCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </div>
            {!activityLogCollapsed && (
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {activityLog.map((entry, idx) => {
                  const colorMap: Record<ActivityType, string> = { created: "#F3F4F6", sent: "#E6E8FE", approved: "#D1FAE5", changes_requested: "#FFF7ED", note: "#F3F4F6" };
                  const iconMap: Record<ActivityType, React.ReactNode> = {
                    created: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round"><rect x="1" y="1" width="10" height="10" rx="2" /><path d="M4 6h4M6 4v4" /></svg>,
                    sent: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517F8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M10.5 1.5L1.5 5.5l3 1.5 1.5 3.5 4.5-9z" /></svg>,
                    approved: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#059669" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="5" /><path d="M3.5 6l2 2 3-3" /></svg>,
                    changes_requested: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#EA580C" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="5" /><path d="M6 4v3M6 8.5v.5" /></svg>,
                    note: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round"><rect x="1.5" y="1.5" width="9" height="9" rx="1.5" /><path d="M3.5 5h5M3.5 7.5h3" /></svg>,
                  };
                  return (
                    <div key={entry.id} style={{ display: "flex", gap: 10, position: "relative" }}>
                      {idx < activityLog.length - 1 && (
                        <div style={{ position: "absolute", left: 11, top: 24, bottom: -4, width: 1, background: "#E5E7EB" }} />
                      )}
                      <div style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", background: colorMap[entry.type], display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2, zIndex: 1 }}>
                        {iconMap[entry.type]}
                      </div>
                      <div style={{ flex: 1, paddingBottom: idx < activityLog.length - 1 ? 14 : 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", lineHeight: 1.4 }}>{entry.message}</div>
                        {entry.user && <div style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)", marginTop: 1 }}>{entry.user}</div>}
                        <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "var(--font-family-base)", marginTop: 2 }}>{entry.timestamp}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Payment History panel */}
          <div style={{ padding: "16px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <PaymentIcon />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px" }}>
                  Payment History
                </span>
              </div>
              <button
                onClick={() => setShowRecordModal(true)}
                style={{ border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--color-content-brand)", fontFamily: "var(--font-family-base)", padding: 0 }}
              >
                Record
              </button>
            </div>

            {paymentRecords.length === 0 ? (
              <div style={{
                border: "1px solid #E5E7EB", borderRadius: 6, padding: "20px 12px",
                textAlign: "center", fontSize: 12, color: "#9CA3AF", fontFamily: "var(--font-family-base)",
              }}>
                No payments recorded yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {paymentRecords.map((p) => {
                  const methodLabel = PAYMENT_METHODS.find((m) => m.value === p.method)?.label ?? p.method;
                  return (
                    <div key={p.id} style={{
                      border: "1px solid #E5E7EB", borderRadius: 6, padding: "10px 12px",
                      display: "flex", flexDirection: "column", gap: 3,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "var(--font-family-base)" }}>
                          {fmtEur(p.amount)}
                        </span>
                        <span style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>
                          {formatIsoDate(p.dateReceived)}
                        </span>
                      </div>
                      <span style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>
                        {methodLabel}{p.reference ? ` · ${p.reference}` : ""}
                      </span>
                      {p.note && (
                        <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "var(--font-family-base)", fontStyle: "italic" }}>
                          {p.note}
                        </span>
                      )}
                    </div>
                  );
                })}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 4 }}>
                  <span style={{ fontSize: 12, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>Outstanding</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: outstandingBalance > 0 ? "#DC2626" : "#059669", fontFamily: "var(--font-family-base)" }}>
                    {fmtEur(outstandingBalance)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <RecordPaymentModal
        isOpen={showRecordModal}
        onClose={() => setShowRecordModal(false)}
        onRecord={handleRecordPayment}
        outstandingBalance={outstandingBalance}
      />

      {showCatalogModal && (
        <CatalogModal
          onClose={() => setShowCatalogModal(false)}
          onAdd={handleAddCatalogItem}
          addedItemIds={new Set(lineItems.flatMap((li) => li.catalogId ? [li.catalogId] : []))}
          contract={activeContract}
        />
      )}

      {selectedContractPanel && (
        <ContractDetailPanel
          contract={selectedContractPanel}
          onClose={() => setSelectedContractPanel(null)}
        />
      )}

      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        docType="Invoice"
        docNumber={invoiceNum}
        contacts={INVOICE_CONTACT_LIST[company] ?? INVOICE_CONTACT_LIST["acme-international"]}
        senderAccounts={MOCK_SENDER_ACCOUNTS}
      />

      <CustomerChangeRequestModal
        isOpen={showChangeRequestModal}
        onClose={() => setShowChangeRequestModal(false)}
        onSubmit={handleSubmitChangeRequest}
        customerName="Micheal Zeigler"
      />

      {showPortalPreview && (() => {
        const contactList = INVOICE_CONTACT_LIST[company] ?? INVOICE_CONTACT_LIST["acme-international"];
        const primaryContact = contactList[0];
        const portalContact = {
          name: primaryContact.name,
          email: primaryContact.email,
          company: COMPANY_DISPLAY_NAMES[company] ?? company,
          initials: primaryContact.initials,
          color: primaryContact.color,
        };
        return (
          <InvoicePortalOverlay
            invoiceNumber={invoiceNum}
            issueDate={issueDate}
            dueDate={dueDate}
            contact={portalContact}
            lineItems={lineItems}
            subTotal={subTotal}
            marginTotal={marginTotal}
            discountTotal={discountTotal}
            grandTotal={grandTotal}
            discountPct={discountPct}
            marginPct={marginPct}
            note={note}
            onClose={() => setShowPortalPreview(false)}
            onRequestChanges={handleSubmitChangeRequest}
          />
        );
      })()}
    </div>
  );
}
